import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTickets } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Ticket, Calendar, MapPin, QrCode, Download, ExternalLink } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const MyTickets = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const res = await getTickets();
      setTickets(res.data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const validTickets = tickets.filter(t => t.status === 'valid');
  const usedTickets = tickets.filter(t => t.status === 'used');

  const TicketCard = ({ ticket }) => {
    const eventDate = new Date(ticket.event_date);
    const isPast = eventDate < new Date();

    return (
      <div className="glass-card overflow-hidden" data-testid={`ticket-${ticket.ticket_id}`}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <Badge variant={ticket.status === 'valid' ? 'default' : 'secondary'}>
                {ticket.status === 'valid' ? 'Válido' : 'Usado'}
              </Badge>
              {isPast && ticket.status === 'valid' && (
                <Badge variant="outline" className="ml-2">Evento pasado</Badge>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {ticket.ticket_id}
            </span>
          </div>

          <h3 className="text-xl font-bold mb-2">{ticket.event_title}</h3>
          
          <div className="space-y-2 text-sm text-muted-foreground mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{format(eventDate, "EEEE, d 'de' MMMM 'de' yyyy - h:mm a", { locale: es })}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>{ticket.event_location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Ticket className="w-4 h-4" />
              <span>{ticket.ticket_type}</span>
            </div>
          </div>

          {/* QR Code */}
          <div className="flex justify-center mb-4">
            <div className="qr-container">
              {ticket.qr_code ? (
                <img
                  src={`data:image/png;base64,${ticket.qr_code}`}
                  alt="Ticket QR Code"
                  className="w-40 h-40"
                />
              ) : (
                <QRCodeSVG
                  value={ticket.qr_data || ticket.ticket_id}
                  size={160}
                  level="H"
                />
              )}
            </div>
          </div>

          <p className="text-xs text-center text-muted-foreground mb-4">
            Muestra este código en la entrada del evento
          </p>

          <div className="flex gap-2">
            <Link to={`/events/${ticket.event_id}`} className="flex-1">
              <Button variant="outline" className="w-full" size="sm">
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver Evento
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="h-8 w-48 bg-muted animate-pulse rounded mb-8" />
          <div className="grid gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-80 bg-muted animate-pulse rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Mis Tickets</h1>
          <p className="text-muted-foreground">
            Gestiona tus entradas a eventos
          </p>
        </div>

        {tickets.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <QrCode className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No tienes tickets</h3>
            <p className="text-muted-foreground mb-6">
              Cuando compres entradas para eventos, aparecerán aquí.
            </p>
            <Link to="/events">
              <Button className="btn-primary">
                Explorar Eventos
              </Button>
            </Link>
          </div>
        ) : (
          <Tabs defaultValue="valid" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="valid" data-testid="tab-valid">
                Válidos ({validTickets.length})
              </TabsTrigger>
              <TabsTrigger value="used" data-testid="tab-used">
                Usados ({usedTickets.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="valid">
              {validTickets.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No tienes tickets válidos
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  {validTickets.map(ticket => (
                    <TicketCard key={ticket.ticket_id} ticket={ticket} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="used">
              {usedTickets.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No tienes tickets usados
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  {usedTickets.map(ticket => (
                    <TicketCard key={ticket.ticket_id} ticket={ticket} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default MyTickets;
